// ---- where a file is ----
// Every model, clip and picture a game fetches goes through here, and nowhere
// else knows where a file comes from. On the website that is nothing: a path
// written against the page ('mawz.glb', 'audio/', '../vowels/audio/') or
// against the site ('/core/tree.glb') comes back as the same file on the same
// site. It exists so that it can be something else: a pack a family's game
// was handed at open names its files from the site's root wherever the page
// is (tata/src/pack.ts), and an app that has downloaded a pack hands back the
// copy on the device (docs/plans/2026-10-31-mobile-apps.md). Either way the
// game asks for the file it always asked for.
//
// The key is the path from the site's root, decoded: '/tata/audio/L_ب.mp3' is
// the same key however the page spelled it, and it is the `path` a pack lists
// the file under (scripts/build-pack.mjs). The resolver turns a key into
// something fetch and the loaders will take; the site's own is the key again,
// encoded for the wire.
export type Resolver=(root:string)=>string;
let resolve:Resolver=root=>encodeURI(root);
export const setResolver=(r:Resolver)=>{resolve=r;};

// What an app keeps, as the game it opens needs it: a kept file by its root
// path, the folders the app itself carries (`here`: a path under one of them
// is the app's own copy), and the site everything else comes from. The store
// builds it (core/store.ts), the app's menu writes it to sessionStorage
// under KEPT_KEY before a game opens (app/src/main.ts), and this module reads
// it once as it loads — so a game opens on a snapshot, and an install that
// finishes under it changes nothing until the next open. The website writes
// none of this, and the site's own resolver stands.
export interface Snapshot{site:string;at:Record<string,string>;here:string[]}
export const KEPT_KEY='danys.kept';
export const resolveFrom=(s:Snapshot):Resolver=>root=>s.at[root]??encodeURI((s.here.some(p=>root.startsWith(p))?'':s.site)+root);
try{const s=sessionStorage.getItem(KEPT_KEY);if(s)resolve=resolveFrom(JSON.parse(s) as Snapshot);}catch(err){}
// Whether a fetch brought the file. On the site that is `ok`. In the app, a
// file the app carries comes over Capacitor's own scheme, which answers a
// media file (an .mp3: every clip) without an HTTP status: the page reads
// status 0 and not ok, and the bytes are all there. A file that is not there
// fails the fetch instead, so 0 is the file. three's loader takes 0 the same
// way; every clip loader takes it, the one-file games' too, and
// scripts/check-audio.js fails on one that does not (app/README.md → How a
// game finds a kept file).
export const got=(r:Response)=>r.ok||r.status===0;
// A path as the game wrote it, against the page or the site, to the URL to
// fetch it from. A data: URL is not a file and goes through untouched.
export function asset(path:string):string{
  if(/^(data|blob):/.test(path))return path;
  return resolve(decodeURI(new URL(path,location.href).pathname));
}
